import { createApi } from '@reduxjs/toolkit/query/react';
import { mergeDataPointsIntoDevices } from 'features/integrations/utils/integrationDataUtils';
import { axiosBaseQuery } from 'services/_infra/rtkBaseQuery';

export const devicesApi = createApi({
  reducerPath: 'devicesApi', // This is the name of the slice in the store
  baseQuery: axiosBaseQuery(), // Custom axios base query (calls apiClient with the query url and params)
  tagTypes: [{ type: 'Device', id: 'CONNECTED_LIST' }], // Used to track when refetches are needed
  endpoints: (builder) => ({
    getConnectedDevices: builder.query({
      // args: { startTime, endTime }
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        const { startTime, endTime } = arg ?? {
          startTime: Date.now() - 24 * 60 * 60 * 1000,
          endTime: Date.now(),
        };
        try {
          const [devicesRes, nodeDataRes] = await Promise.all([
            fetchWithBQ({ url: '/getConnectedDevices_v2', params: { startTime, endTime } }),
            fetchWithBQ({
              url: '/getNodeDataByTimestamps_v2',
              params: { startTime, endTime },
            }),
          ]);

          if (devicesRes.error) {
            console.error('Error fetching connected devices:', devicesRes.error);
            return { error: devicesRes.error };
          }

          if (nodeDataRes.error) {
            console.error('Error fetching node data for connected devices:', nodeDataRes.error);
            return { error: nodeDataRes.error };
          }

          const devices = devicesRes.data?.result ?? [];
          const nodeData = nodeDataRes.data?.result ?? [];
          const merged = mergeDataPointsIntoDevices(devices, nodeData);
          return { data: merged };
        } catch (err) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: err?.message ?? 'Unknown error in getConnectedDevices query',
            },
          };
        }
      },
      keepUnusedDataFor: 60 * 3,
      providesTags: [{ type: 'Device', id: 'CONNECTED_LIST' }],
    }),
    addConnectedDevice: builder.mutation({
      query: (device) => ({
        url: '/addConnectedDevice_v2',
        method: 'POST',
        data: { device },
      }),
    }),
    updateConnectedDevice: builder.mutation({
      query: (device) => ({
        url: '/updateConnectedDeviceProperties_v2',
        method: 'POST',
        data: { device },
      }),
    }),
    removeConnectedDevice: builder.mutation({
      query: (device) => ({
        url: '/removeConnectedDevice_v2',
        method: 'DELETE',
        data: { id: device.id },
      }),
    }),
    getAllAvailableDeviceTypes: builder.query({
      query: () => ({
        url: '/getAllAvailableDeviceTypes_v2',
      }),
      transformResponse: (response) => response.result || {}, // Extract the result from the response
    }),
    changeAwareDeviceSettings: builder.mutation({
      query: (deviceSettings) => ({
        url: '/changeAwareDeviceSettings_v2',
        method: 'POST',
        data: { deviceSettings },
      }),
      invalidatesTags: [{ type: 'Device', id: 'CONNECTED_LIST' }], // Tells RTK Query to refetch any queries that provide this tag
    }),
    killswitchDevice: builder.mutation({
      query: ({ id, state }) => ({
        url: '/setKillswitchDevices_v2',
        method: 'POST',
        data: { id, state: Number(state) },
      }),
      invalidatesTags: [{ type: 'Device', id: 'CONNECTED_LIST' }], // Tells RTK Query to refetch any queries that provide this tag
    }),
  }),
});

export const {
  useGetAllAvailableDeviceTypesQuery,
  useGetConnectedDevicesQuery,
  useAddConnectedDeviceMutation,
  useUpdateConnectedDeviceMutation,
  useRemoveConnectedDeviceMutation,
  useChangeAwareDeviceSettingsMutation,
  useKillswitchDeviceMutation,
} = devicesApi;
